/**
 * The firm form, as `POST /v1/firms` takes it. Only the conversions JSON
 * needs happen here (a chosen feed, a whole number of orders, symbol lists
 * split); every rule of a firm is the API's, and its sentence is the answer.
 */

import type { CreateFirmRequest } from "./api";

/** What staff typed, field by field. */
export interface FirmForm {
  name: string;
  slug: string;
  /** The address of the first admin's Noctant login. */
  firstAdmin: string;
  /** The chosen feed's key; empty until one is chosen. */
  feedKey: string;
  marginRequirement: string;
  maxPositionSize: string;
  maxOpenOrders: string;
  marginCallLevel: string;
  stopOutLevel: string;
  reservePendingOrderMargin: boolean;
  allowedSymbols: string;
  blockedSymbols: string;
}

export const EMPTY_FIRM_FORM: FirmForm = {
  name: "",
  slug: "",
  firstAdmin: "",
  feedKey: "",
  marginRequirement: "",
  maxPositionSize: "",
  maxOpenOrders: "",
  marginCallLevel: "",
  stopOutLevel: "",
  reservePendingOrderMargin: false,
  allowedSymbols: "",
  blockedSymbols: "",
};

export type FirmRequest =
  | { ok: true; request: CreateFirmRequest }
  | { ok: false; error: string };

/** A whole number as typed, or none. */
function whole(text: string): number | null {
  const trimmed = text.trim();
  return /^-?\d+$/.test(trimmed) && Number.isSafeInteger(Number(trimmed))
    ? Number(trimmed)
    : null;
}

/** A list typed with commas or spaces between its symbols. */
function symbols(text: string): string[] {
  return text.split(/[\s,]+/).filter((symbol) => symbol !== "");
}

export function firmRequest(form: FirmForm): FirmRequest {
  const feedKey = whole(form.feedKey);
  if (feedKey === null) {
    return { ok: false, error: "Choose the feed the firm trades on." };
  }
  const maxOpenOrders = whole(form.maxOpenOrders);
  if (maxOpenOrders === null) {
    return { ok: false, error: "Max open orders must be a whole number." };
  }
  return {
    ok: true,
    request: {
      name: form.name,
      slug: form.slug,
      first_admin: form.firstAdmin,
      feed_key: feedKey,
      risk_terms: {
        margin_requirement: form.marginRequirement,
        max_position_size: form.maxPositionSize,
        max_open_orders: maxOpenOrders,
        margin_call_level: form.marginCallLevel,
        stop_out_level: form.stopOutLevel,
        reserve_pending_order_margin: form.reservePendingOrderMargin,
        allowed_symbols: symbols(form.allowedSymbols),
        blocked_symbols: symbols(form.blockedSymbols),
      },
    },
  };
}
